import React, { FC, Fragment } from 'react';

import { PrimaryMarketProject, ProjectGroupInfo, Reservation, StatusKey } from 'api-client';
import { MetricProgressBar, Money } from 'components/common';
import { ChevronIcon } from 'components/common/icons';
import { formatPercents } from 'formatters';
import { useToggle } from 'hooks';
import { cn, declOfNum } from 'utils';

import { buildGroupSegments, groupSizeLabel } from '../../groupSegments';
import { PrimaryMarketMobileCard } from '../PrimaryMarketMobileCard';
import styles from './PrimaryMarketGroupMobileCard.module.scss';

interface Props {
  group: ProjectGroupInfo;
  members: Array<PrimaryMarketProject>;
  statusKey: StatusKey;
  reservations: Map<string, Reservation>;
}

/**
 * Мобильный аналог строки группы: те же агрегаты, та же разбитая по проектам полоса и те
 * же подписи метрик, что и в карточке проекта. Раскрытые проекты уходят на утопленную
 * поверхность с отступом — так же, как на десктопе.
 */
const PrimaryMarketGroupMobileCard: FC<Props> = ({ group, members, statusKey, reservations }) => {
  const [expanded, toggleExpanded] = useToggle();
  const isPaidTable = statusKey === 'Завершен';
  const membersId = `project-group-mobile-${group.uuid}`;

  return (
    <Fragment>
      <article className={styles.card}>
        <div className={styles.header}>
          <div className={styles.name}>{group.name}</div>
          <div className={styles.count}>{groupSizeLabel(group.memberCount)}</div>
        </div>

        <div className={styles.metrics}>
          <div className={styles.metric}>
            <span className={styles.metricLabel}>Сумма сбора</span>
            <span className={styles.metricValue}>
              <Money money={group.totalDebtPrice} compact />
            </span>
          </div>
          <div className={styles.metric}>
            <span className={styles.metricLabel}>Прогноз взыскания</span>
            <span className={styles.metricValue}>
              <Money money={group.totalRecoveryForecast} compact />
            </span>
          </div>
          <div className={styles.metric}>
            <span className={styles.metricLabel}>ROI / IRR</span>
            <span className={styles.metricValue}>
              {`≈ ${formatPercents(group.weightedSimpleInterestRate, 1, true)} / ${formatPercents(
                group.weightedInterestRate,
                1,
                true
              )}`}
            </span>
          </div>
          <div className={styles.metric}>
            <span className={styles.metricLabel}>{isPaidTable ? 'Факт. срок' : 'Срок'}</span>
            <span className={styles.metricValue}>
              {`≈ ${group.weightedInitialTerm.toFixed(0)} мес.`}
            </span>
          </div>
        </div>

        <MetricProgressBar
          currentValue={<Money money={group.totalInvestedSum} compact />}
          totalValue={<Money money={group.totalDebtPrice} compact />}
          currentLabel={isPaidTable ? 'Взыскано' : 'Собрано'}
          totalLabel={isPaidTable ? 'Прогноз взыскания' : 'Сумма сбора'}
          segments={buildGroupSegments(group)}
        />

        <button
          type="button"
          className={styles.disclosure}
          onClick={toggleExpanded}
          aria-expanded={expanded}
          aria-controls={membersId}
        >
          {expanded
            ? 'Свернуть'
            : `Показать ${group.memberCount} ${declOfNum(group.memberCount, [
                'проект',
                'проекта',
                'проектов',
              ])}`}
          <ChevronIcon
            className={cn(styles.chevron, expanded && styles.chevronExpanded)}
            direction="down"
            size={16}
            color="currentColor"
          />
        </button>
      </article>

      <div className={styles.members} id={membersId} hidden={!expanded}>
        {members.map((member) => (
          <PrimaryMarketMobileCard
            project={member}
            statusKey={statusKey}
            reservation={reservations.get(member.uuid)}
            key={member.uuid}
          />
        ))}
      </div>
    </Fragment>
  );
};

export default PrimaryMarketGroupMobileCard;
